
import { openDB, DBSchema, IDBPDatabase } from 'idb';

export interface ListingNotification {
    id: string; // Unique ID (from backend or generated)
    title: string;
    body: string; // Formatted summary
    url: string;
    timestamp: number;
    read: boolean;
    data?: any; // Raw data if needed
}

interface ListingsDB extends DBSchema {
    notifications: {
        key: string;
        value: ListingNotification;
        indexes: { 'by-timestamp': number };
    };
}

const DB_NAME = 'listing-feed';
const DB_VERSION = 1;

export class StorageManager {
    private dbPromise: Promise<IDBPDatabase<ListingsDB>>;
    private quotaLimit: number;
    private dbName: string;
    private channel: BroadcastChannel;

    constructor(quotaLimit = 500, dbName = DB_NAME) {
        this.quotaLimit = quotaLimit;
        this.dbName = dbName;
        this.channel = new BroadcastChannel('feed_updates');
        this.dbPromise = openDB<ListingsDB>(this.dbName, DB_VERSION, {
            upgrade(db) {
                const store = db.createObjectStore('notifications', {
                    keyPath: 'id',
                });
                store.createIndex('by-timestamp', 'timestamp');
            },
        });
    }

    async addNotification(notification: ListingNotification): Promise<void> {
        const db = await this.dbPromise;
        await db.put('notifications', notification);
        await this.enforceQuota();
        this.notifyUpdate('NEW_LISTING', notification.id);
    }

    async getNotifications(limit = 50, offset = 0): Promise<ListingNotification[]> {
        const db = await this.dbPromise;
        // Use a transaction to get sorted data
        // Descending order (newest first)
        const tx = db.transaction('notifications', 'readonly');
        const index = tx.store.index('by-timestamp');

        let cursor = await index.openCursor(null, 'prev');
        const results: ListingNotification[] = [];
        let skipped = 0;

        while (cursor && results.length < limit) {
            if (skipped < offset) {
                skipped++;
                cursor = await cursor.continue();
                continue;
            }
            results.push(cursor.value);
            cursor = await cursor.continue();
        }

        return results;
    }

    async getUnreadCount(): Promise<number> {
        const db = await this.dbPromise;
        // This is inefficient if we don't have an index on 'read', but for <500 items it's fine.
        // Or we can add an index later.
        const all = await db.getAll('notifications');
        return all.filter(n => !n.read).length;
    }

    async markAsRead(id: string): Promise<void> {
        const db = await this.dbPromise;
        const notification = await db.get('notifications', id);
        if (notification) {
            notification.read = true;
            await db.put('notifications', notification);
            this.notifyUpdate('LISTING_READ', id);
        }
    }

    async deleteNotification(id: string): Promise<void> {
        const db = await this.dbPromise;
        await db.delete('notifications', id);
        this.notifyUpdate('LISTING_DELETED', id);
    }

    async deleteAllNotifications(): Promise<void> {
        const db = await this.dbPromise;
        const tx = db.transaction('notifications', 'readwrite');
        await tx.store.clear();
        await tx.done;
        this.notifyUpdate('ALL_DELETED');
    }

    async markAllAsRead(): Promise<void> {
        const db = await this.dbPromise;
        const tx = db.transaction('notifications', 'readwrite');
        const store = tx.store;
        let cursor = await store.openCursor();

        while (cursor) {
            const update = cursor.value;
            if (!update.read) {
                update.read = true;
                await cursor.update(update);
            }
            cursor = await cursor.continue();
        }
        await tx.done;
        this.notifyUpdate('ALL_READ');
    }

    private async enforceQuota(limit = this.quotaLimit): Promise<void> {
        const db = await this.dbPromise;
        const count = await db.count('notifications');

        if (count > limit) {
            // Delete oldest
            const tx = db.transaction('notifications', 'readwrite');
            const index = tx.store.index('by-timestamp');
            let cursor = await index.openCursor(null, 'next'); // Oldest first

            let toDelete = count - limit;
            while (cursor && toDelete > 0) {
                await cursor.delete();
                toDelete--;
                cursor = await cursor.continue();
            }
            await tx.done;
        }
    }

    private notifyUpdate(type: string, id?: string) {
        this.channel.postMessage({ type, id });
    }
}

export const storageManager = new StorageManager();
